import { useMemo, useState } from "react";
import type { SimulationBlock } from "@shared/lessonBlocks";
import { RichText } from "../RichText";
import { useCheck, CheckFooter, type CheckResult } from "../CheckShell";

interface Props {
  block: SimulationBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

type Vals = Record<string, number>;

/**
 * Every model is computed here, in code, from named inputs. There is no formula string anywhere
 * in the block schema and nothing is evaluated, so a lesson author cannot inject code and a
 * mistyped formula cannot reach a student through content.
 */
const G = 9.81;
function compute(model: SimulationBlock["model"], v: Vals): Vals {
  const rad = (d: number) => (d * Math.PI) / 180;
  switch (model) {
    case "projectile": {
      const u = v.u ?? 0, a = rad(v.angle ?? 0);
      return {
        range: (u * u * Math.sin(2 * a)) / G,
        height: (u * u * Math.sin(a) ** 2) / (2 * G),
        time: (2 * u * Math.sin(a)) / G,
      };
    }
    case "ohms-law": {
      const V = v.V ?? 0, R = Math.max(v.R ?? 1, 0.0001);
      return { I: V / R, P: (V * V) / R };
    }
    case "resistors": {
      const r1 = Math.max(v.R1 ?? 1, 0.0001), r2 = Math.max(v.R2 ?? 1, 0.0001);
      const parallel = (v.arrangement ?? 0) >= 0.5;
      return { total: parallel ? (r1 * r2) / (r1 + r2) : r1 + r2, arrangement: parallel ? 1 : 0 };
    }
    case "pendulum": {
      const L = Math.max(v.length ?? 0.1, 0.01), g = Math.max(v.g ?? G, 0.1);
      const T = 2 * Math.PI * Math.sqrt(L / g);
      return { period: T, frequency: 1 / T };
    }
    case "refraction": {
      const n1 = Math.max(v.n1 ?? 1, 0.01), n2 = Math.max(v.n2 ?? 1, 0.01), i = v.angle ?? 0;
      const s = (n1 / n2) * Math.sin(rad(i));
      // sin r > 1 means the ray never leaves: total internal reflection.
      const tir = s > 1;
      return {
        refracted: tir ? NaN : (Math.asin(Math.min(s, 1)) * 180) / Math.PI,
        critical: n1 > n2 ? (Math.asin(n2 / n1) * 180) / Math.PI : NaN,
        tir: tir ? 1 : 0,
      };
    }
    default: {
      const f = Math.max(v.frequency ?? 0, 0), w = Math.max(v.wavelength ?? 0, 0.0001);
      return { speed: f * w, period: f > 0 ? 1 / f : NaN };
    }
  }
}

const AX = "hsl(var(--muted-foreground))";
const BRAND = "hsl(var(--primary))";

function Diagram({ model, v, out }: { model: SimulationBlock["model"]; v: Vals; out: Vals }) {
  const box = "w-full h-auto";
  const vb = "0 0 320 160";

  if (model === "projectile") {
    const R = out.range || 0, H = out.height || 0;
    const maxR = 260, sx = R > 0 ? Math.min(280 / R, 8) : 1;
    const w = Math.min(R * sx, maxR), h = Math.min(H * sx, 110);
    const pts = Array.from({ length: 25 }, (_, i) => {
      const t = i / 24, x = 20 + w * t, y = 140 - 4 * h * t * (1 - t);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    return (
      <svg viewBox={vb} className={box} role="img" aria-label={`Projectile path, range ${R.toFixed(1)} metres`}>
        <line x1="20" y1="140" x2="310" y2="140" stroke={AX} strokeWidth="2" />
        <polyline points={pts} fill="none" stroke={BRAND} strokeWidth="3" strokeLinecap="round" />
        <circle cx={20 + w} cy="140" r="5" fill={BRAND} />
        <text x="20" y="155" fontSize="11" fill={AX}>launch</text>
        <text x={Math.min(20 + w, 250)} y="155" fontSize="11" fill={AX}>{R.toFixed(1)} m</text>
      </svg>
    );
  }

  if (model === "ohms-law") {
    const I = out.I || 0;
    return (
      <svg viewBox={vb} className={box} role="img" aria-label={`Circuit, current ${I.toFixed(2)} amps`}>
        <rect x="30" y="30" width="260" height="100" fill="none" stroke={AX} strokeWidth="3" rx="6" />
        <rect x="130" y="18" width="60" height="24" fill="hsl(var(--background))" stroke={BRAND} strokeWidth="3" rx="3" />
        <text x="160" y="35" fontSize="12" textAnchor="middle" fill={BRAND} fontWeight="bold">{(v.R ?? 0).toFixed(0)}Ω</text>
        <line x1="60" y1="118" x2="60" y2="142" stroke={AX} strokeWidth="3" />
        <line x1="72" y1="124" x2="72" y2="136" stroke={AX} strokeWidth="5" />
        <text x="40" y="155" fontSize="11" fill={AX}>{(v.V ?? 0).toFixed(1)} V</text>
        {Array.from({ length: 5 }, (_, i) => (
          <circle key={i} cx={70 + i * 50} cy="30" r={Math.min(2 + I, 6)} fill={BRAND} opacity="0.85" />
        ))}
        <text x="290" y="90" fontSize="12" textAnchor="end" fill={BRAND} fontWeight="bold">{I.toFixed(2)} A</text>
      </svg>
    );
  }

  if (model === "resistors") {
    const par = (out.arrangement ?? 0) >= 0.5;
    return (
      <svg viewBox={vb} className={box} role="img" aria-label={par ? "Resistors in parallel" : "Resistors in series"}>
        <line x1="15" y1="80" x2="60" y2="80" stroke={AX} strokeWidth="3" />
        <line x1="260" y1="80" x2="305" y2="80" stroke={AX} strokeWidth="3" />
        {par ? (
          <>
            <path d="M60 80 L60 45 L100 45" stroke={AX} strokeWidth="3" fill="none" />
            <path d="M60 80 L60 115 L100 115" stroke={AX} strokeWidth="3" fill="none" />
            <path d="M220 45 L260 45 L260 80" stroke={AX} strokeWidth="3" fill="none" />
            <path d="M220 115 L260 115 L260 80" stroke={AX} strokeWidth="3" fill="none" />
            <rect x="100" y="33" width="120" height="24" fill="hsl(var(--background))" stroke={BRAND} strokeWidth="3" rx="3" />
            <text x="160" y="50" fontSize="12" textAnchor="middle" fill={BRAND} fontWeight="bold">{(v.R1 ?? 0).toFixed(0)}Ω</text>
            <rect x="100" y="103" width="120" height="24" fill="hsl(var(--background))" stroke={BRAND} strokeWidth="3" rx="3" />
            <text x="160" y="120" fontSize="12" textAnchor="middle" fill={BRAND} fontWeight="bold">{(v.R2 ?? 0).toFixed(0)}Ω</text>
          </>
        ) : (
          <>
            <line x1="60" y1="80" x2="305" y2="80" stroke={AX} strokeWidth="3" />
            <rect x="70" y="68" width="90" height="24" fill="hsl(var(--background))" stroke={BRAND} strokeWidth="3" rx="3" />
            <text x="115" y="85" fontSize="12" textAnchor="middle" fill={BRAND} fontWeight="bold">{(v.R1 ?? 0).toFixed(0)}Ω</text>
            <rect x="180" y="68" width="90" height="24" fill="hsl(var(--background))" stroke={BRAND} strokeWidth="3" rx="3" />
            <text x="225" y="85" fontSize="12" textAnchor="middle" fill={BRAND} fontWeight="bold">{(v.R2 ?? 0).toFixed(0)}Ω</text>
          </>
        )}
        <text x="160" y="152" fontSize="12" textAnchor="middle" fill={AX}>{par ? "parallel" : "series"}</text>
      </svg>
    );
  }

  if (model === "pendulum") {
    const L = v.length ?? 1;
    const len = 30 + Math.min(L, 3) * 30;
    const a = 25 * Math.PI / 180;
    const bx = 160 + len * Math.sin(a), by = 25 + len * Math.cos(a);
    return (
      <svg viewBox={vb} className={box} role="img" aria-label={`Pendulum of length ${L.toFixed(2)} metres`}>
        <line x1="90" y1="25" x2="230" y2="25" stroke={AX} strokeWidth="4" />
        <path d={`M ${160 - len * Math.sin(a)} ${25 + len * Math.cos(a)} A ${len} ${len} 0 0 1 ${bx} ${by}`}
              stroke={AX} strokeWidth="1.5" strokeDasharray="4 4" fill="none" opacity="0.6" />
        <line x1="160" y1="25" x2={bx} y2={by} stroke={BRAND} strokeWidth="2.5" />
        <circle cx={bx} cy={by} r="11" fill={BRAND} />
        <text x="168" y={25 + len / 2} fontSize="11" fill={AX}>{L.toFixed(2)} m</text>
      </svg>
    );
  }

  if (model === "refraction") {
    const i = v.angle ?? 0, r = out.refracted, tir = (out.tir ?? 0) >= 0.5;
    const rad = (d: number) => (d * Math.PI) / 180;
    const ix = 160 - 70 * Math.sin(rad(i)), iy = 80 - 70 * Math.cos(rad(i));
    const rx = tir ? 160 + 70 * Math.sin(rad(i)) : 160 + 70 * Math.sin(rad(r || 0));
    const ry = tir ? 80 - 70 * Math.cos(rad(i)) : 80 + 70 * Math.cos(rad(r || 0));
    return (
      <svg viewBox={vb} className={box} role="img" aria-label={tir ? "Total internal reflection" : `Ray refracts to ${(r || 0).toFixed(1)} degrees`}>
        <rect x="0" y="80" width="320" height="80" fill={BRAND} opacity="0.07" />
        <line x1="10" y1="80" x2="310" y2="80" stroke={AX} strokeWidth="2.5" />
        <line x1="160" y1="10" x2="160" y2="150" stroke={AX} strokeWidth="1" strokeDasharray="4 4" opacity="0.7" />
        <line x1={ix} y1={iy} x2="160" y2="80" stroke={BRAND} strokeWidth="3" />
        <line x1="160" y1="80" x2={rx} y2={ry} stroke={tir ? "hsl(var(--destructive))" : BRAND} strokeWidth="3" />
        <text x="8" y="72" fontSize="11" fill={AX}>n₁ {(v.n1 ?? 1).toFixed(2)}</text>
        <text x="8" y="98" fontSize="11" fill={AX}>n₂ {(v.n2 ?? 1).toFixed(2)}</text>
        {tir && <text x="312" y="120" fontSize="12" textAnchor="end" fill="hsl(var(--destructive))" fontWeight="bold">total internal reflection</text>}
      </svg>
    );
  }

  // wave. One drawn cycle must get WIDER as the wavelength grows, or the picture says the opposite
  // of the words. The scale is schematic (the SVG is 320 px wide and the models run from a few
  // centimetres to several metres), clamped so a short wave stays countable and a long one still
  // shows a full cycle instead of running off the picture. The lambda ruler below the wave is drawn
  // from the same number, so ruler and wave can never disagree.
  const wl = Math.max(v.wavelength ?? 1, 0.05);
  const pxPerCycle = Math.min(Math.max(38 * wl, 24), 300);
  const pts = Array.from({ length: 161 }, (_, k) => {
    const x = k * 2;
    const y = 80 - 38 * Math.sin((x / pxPerCycle) * 2 * Math.PI);
    return `${x},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={vb} className={box} role="img" aria-label={`Wave of wavelength ${wl.toFixed(2)} metres`}>
      <line x1="0" y1="80" x2="320" y2="80" stroke={AX} strokeWidth="1" opacity="0.5" />
      <polyline points={pts} fill="none" stroke={BRAND} strokeWidth="3" strokeLinecap="round" />
      <line x1="0" y1="132" x2={pxPerCycle} y2="132" stroke={AX} strokeWidth="2" markerStart="" />
      <text x={pxPerCycle / 2} y="148" fontSize="11" textAnchor="middle" fill={AX}>λ = {wl.toFixed(2)} m</text>
    </svg>
  );
}

export function SimulationBlockView({ block, onResult, alreadyDone }: Props) {
  const graded = !!block.challenge;
  const check = useCheck(onResult, { alreadyDone });
  const [vals, setVals] = useState<Vals>(() =>
    Object.fromEntries(block.controls.map((c) => [c.key, c.initial] as const)));

  const out = useMemo(() => compute(block.model, vals), [block.model, vals]);
  const done = graded && check.locked;

  const hit = block.challenge
    ? Math.abs((out[block.challenge.readout] ?? NaN) - block.challenge.target) <= block.challenge.tolerance
    : false;

  const fmt = (n: number, d: number) => (Number.isFinite(n) ? n.toFixed(d) : "–");

  return (
    <div className="space-y-4">
      <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />

      <div className="rounded-2xl border-2 border-border bg-card p-3">
        <Diagram model={block.model} v={vals} out={out} />
      </div>

      {/* Readouts. tabular-nums so the numbers do not jitter while a slider is dragged. */}
      <div className={`grid gap-2 ${block.readouts.length > 2 ? "grid-cols-3" : "grid-cols-2"}`}>
        {block.readouts.map((r) => (
          <div key={r.key} className="rounded-xl bg-primary/10 border border-primary/25 px-3 py-2 text-center">
            <div className="text-[11px] font-medium text-muted-foreground leading-tight">{r.label}</div>
            <div className="text-lg md:text-xl font-bold tabular-nums text-primary">
              {fmt(out[r.key] ?? NaN, r.decimals)}
              {r.unit && <span className="text-xs font-semibold ml-0.5">{r.unit}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Sliders. h-11 gives a touch target that works with a thumb on a phone. */}
      <div className="space-y-3">
        {block.controls.map((c) => (
          <div key={c.key}>
            <div className="flex items-baseline justify-between mb-1">
              <label htmlFor={`sim-${block.id}-${c.key}`} className="text-sm font-semibold">{c.label}</label>
              <span className="text-sm font-bold tabular-nums text-primary">
                {vals[c.key]?.toFixed(String(c.step).includes(".") ? String(c.step).split(".")[1].length : 0)}
                {c.unit && <span className="ml-0.5 text-xs">{c.unit}</span>}
              </span>
            </div>
            <input
              id={`sim-${block.id}-${c.key}`}
              type="range"
              className="w-full h-11 accent-primary touch-manipulation"
              min={c.min}
              max={c.max}
              step={c.step}
              value={vals[c.key] ?? c.initial}
              disabled={done}
              onChange={e => {
                if (check.status === "wrong") check.retry();
                setVals(p => ({ ...p, [c.key]: Number(e.target.value) }));
              }}
              data-testid={`sim-control-${c.key}`}
            />
          </div>
        ))}
      </div>

      {block.challenge && (
        <>
          <div className="rounded-xl border border-amber-400/60 bg-amber-50 dark:bg-amber-950/20 p-3">
            <RichText text={block.challenge.text} className="text-sm" />
          </div>
          <CheckFooter
            check={check}
            canSubmit
            onSubmit={() => check.submit(hit)}
            hint={block.hint}
            explanation={block.explanation}
          />
        </>
      )}

      {!block.challenge && block.explanation && (
        <div className="rounded-xl bg-muted/40 p-3">
          <RichText text={block.explanation} className="text-sm" />
        </div>
      )}
    </div>
  );
}
